import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, fromEvent, timer } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Api } from 'app/core/models/Api.model';
import { AuthService } from './auth.service';

/**
 * Revalida contra el backend que la sesión siga viva.
 *
 * El backend ya rechaza al usuario desactivado (SimpleJWT valida is_active en
 * cada request) y el AuthInterceptor ya cierra sesión ante un 401. Lo que
 * faltaba era que alguien preguntara: sin esto, quien esté parado en una
 * pantalla ya cargada sigue viéndola hasta que haga clic en algo que pida
 * datos — el AuthGuard no consulta al backend, solo mira la expiración local.
 *
 * No cierra sesión por su cuenta: solo dispara la petición. Del 401 se encarga
 * el AuthInterceptor, y un error de red o un 5xx se ignoran a propósito para no
 * sacar a nadie por un hipo de conexión.
 */
@Injectable({
    providedIn: 'root'
})
export class SessionWatchService {
    /** Cada cuánto se revalida. Ver comentario de `verificar()` sobre el costo. */
    private readonly INTERVALO_MS = 2 * 60 * 1000;

    private readonly destroy$ = new Subject<void>();
    private vigilando = false;

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthService,
        private readonly ngZone: NgZone
    ) {
    }

    /**
     * Inicia la vigilancia de la sesión
     */
    startWatching(): void {
        if (this.vigilando) {
            return;
        }

        this.vigilando = true;

        // Fuera de Angular: el timer no debe disparar change detection cada
        // dos minutos por sí solo.
        this.ngZone.runOutsideAngular(() => {
            timer(this.INTERVALO_MS, this.INTERVALO_MS)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.ngZone.run(() => this.verificar()));

            // Al volver a la pestaña, verificar de inmediato. Es el momento que
            // más importa: es cuando el usuario intenta seguir trabajando, y es
            // lo que hace que pausar en segundo plano no cueste nada.
            fromEvent(document, 'visibilitychange')
                .pipe(
                    filter(() => !document.hidden),
                    takeUntil(this.destroy$)
                )
                .subscribe(() => this.ngZone.run(() => this.verificar()));
        });
    }

    /**
     * Detiene la vigilancia (al cerrar sesión)
     */
    stopWatching(): void {
        this.vigilando = false;
        this.destroy$.next();
    }

    /**
     * Pregunta al backend si la sesión sigue siendo válida
     */
    private verificar(): void {
        // Pestaña en segundo plano: no gastar peticiones. La mayoría de las
        // pestañas de la plataforma están así, y al volver se verifica de una.
        if (document.hidden) {
            return;
        }

        // Sin token no hay nada que revalidar; de eso se encarga el AuthGuard.
        if (!this.authService.accessToken) {
            return;
        }

        this.http.get(Api.urlSessionPing).subscribe({
            error: () => {
                // Silencio deliberado. Un 401 ya lo maneja el AuthInterceptor
                // (signOut + redirect); cualquier otro error NO debe cerrar la
                // sesión del usuario.
            }
        });
    }
}
